import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import * as s from "pages/recorders/RecordersStyled";
import TableButton from "components/atoms/buttons/TableButton";
import Type from "components/atoms/text/labels/Type";
import ONLINE_ICON from "assets/icons/online.svg";
import OFFLINE_ICON from "assets/icons/offline.svg";
import TableTitle from "components/blocks/table/TableTitle";
import { useSearchParams } from "react-router-dom";
import {
  BasePagination,
  CustomError,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useQuery } from "react-query";
import { getRecorderList } from "api/recorderAPI";
import {
  convertType,
  downloadFromUrl,
  formatNumber,
  isVersionGreater,
} from "utils/functions";
import Status, {
  StatusName,
  StatusValue,
} from "components/blocks/status/Status";
import {
  LicenseCount,
  MergedRecorder,
  MergedRecorderListResponse,
  RecorderConfig,
  RecorderConfigResponse,
  SubscriptionInfo,
} from "api/interfaces/recorderInterface.interface";
import WebAppLink from "components/blocks/link/WebAppLink";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import { useMenu } from "hook/useMenu";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import * as mediaQuery from "components/MediaQuery";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { useFlag } from "@unleash/proxy-client-react";
import useStatusMutation from "hook/useStatusMutation";
import { postInventoryRecorderConfig } from "api/reportAPI";
import useApiError from "hook/useApiError";
import { FaDownload, FaInfoCircle, FaSync } from "react-icons/fa";
import { WithTooltip } from "components/blocks/WithTooltip";
import ConfigurationPresenter from "./ConfigurationPresenter";
import {
  DataRow,
  mergedRecorderToDataRow,
} from "pages/recorders/recorders/list/RecorderListContainer";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { FIRMWARE_FEATURE_KEY } from "api/interfaces/managerInterface.interface";
import { postFirmwareVersionInfo } from "api/manageAPI";

const statusInitValue = {
  totalTitle: "Recorders",
  totalValue: 0,
  items: [
    <>
      <s.Label>
        <img src={ONLINE_ICON} alt="online icon" />
        <StatusName>Online</StatusName>
      </s.Label>
      <StatusValue>0</StatusValue>
    </>,
    <>
      <s.Label>
        <img src={OFFLINE_ICON} alt="offline icon" />
        <StatusName>Offline</StatusName>
      </s.Label>
      <StatusValue>0</StatusValue>
    </>,
  ],

  cloudStatus: false,
  autoUpdate: false,
};
const initialQueryinfo = {
  ...baseQueryInfo,
  pageLimit: 25,
  sortType: "mergedSystemName",
  type: null,
};
interface Props {
  context?: Context;
}

interface Context {
  keyword: string;
  setLicenseCount: (licenseCount: LicenseCount) => void;
  setSubscriptionInfo: (subscriptionInfo: SubscriptionInfo) => void;
  setTitleComponent: (titleComponent: string) => void;
  setCenterChildren: (centerChildren: React.ReactNode) => void;
}
export default function ConfigurationContainer(props: Props): JSX.Element {
  const [centerChildren, setCenterChildren] = useState<React.ReactNode | null>(
    null
  );

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const configDownloadVersion = useQuery(
    ["configDownloadVersion"],
    () =>
      postFirmwareVersionInfo({
        featureKey: FIRMWARE_FEATURE_KEY.COVA_CONFIG_DOWNLOAD,
        displayType: "COVA",
      }),
    {
      enabled: !!selectedAccount.accountId,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );
  const isCovaConfigEnable = useFlag("feature_report_inventory_cova_config");

  const { handleErrorNotify, lang, notifyError } = useApiError();
  const [recorder, setRecorder] = useState<DataRow[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({});

  const recorderQueryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...initialQueryinfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      type: "COVA",
    };
  }, [searchParams, pageInfo]);

  // queryInfo를 바꾸고 싶을 때는 이 함수를 통해 searchParams만 업데이트
  const updateQueryInfo = useCallback(
    (partial: Partial<BasePagination>) => {
      let shouldReplace = false;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(partial).forEach(([key, value]) => {
            if (
              key === "pageNum" &&
              prev.get(key) === null &&
              Number(value) === 0
            ) {
              next.delete(key);
              return;
            }

            if (value === null || value === undefined || value === "") {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          });
          if (next.toString() === prev.toString()) {
            return prev;
          }
          shouldReplace = true;
          return next;
        },
        { replace: shouldReplace }
      );
    },
    [setSearchParams]
  );
  const recorderQueryKey = useQueryKey(recorderQueryInfo);
  const [configureInfo, setIsConfigureInfo] = useState<RecorderConfig | null>(
    null
  );
  const [status, setStatus] = useState(statusInitValue);

  const isMobile = mediaQuery.useIsMobile();
  const isBigScreen = mediaQuery.useIsBigScreen();

  const initialData = () => {
    setRecorder([]);
    setStatus(statusInitValue);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { refetch: refetchRecorder } = useQuery(
    ["recorder", recorderQueryKey, selectedAccount.accountId],
    () =>
      getRecorderList({
        payload: recorderQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      enabled: isCovaConfigEnable,
      //refetchInterval: 10000,
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: MergedRecorderListResponse) => {
        if (res.error !== 0 || !res.result) {
          initialData();
          return;
        }

        setRecorder(
          (res.result.mergedSystemList || []).map(
            (recorder: MergedRecorder): DataRow & { key: string } => {
              return {
                ...mergedRecorderToDataRow(recorder),
                key: recorder.mergedSystemId,
              };
            }
          )
        );

        setStatus({
          totalTitle: "Recorders",
          totalValue: res.result.summary.totalRecorderCount,
          items: [
            <>
              <s.Label>
                <img src={ONLINE_ICON} alt="online icon" width={16} />
                <StatusName>Online</StatusName>
              </s.Label>
              <StatusValue>
                {formatNumber(res.result.summary.onlineStatusCount)}
              </StatusValue>
            </>,
            <>
              <s.Label>
                <img src={OFFLINE_ICON} alt="offline icon" width={16} />
                <StatusName>Offline</StatusName>
              </s.Label>
              <StatusValue>
                {formatNumber(
                  res.result.summary.totalRecorderCount -
                    res.result.summary.onlineStatusCount
                )}
              </StatusValue>
            </>,
          ],

          cloudStatus: res.result.summary.cloudStatus,
          autoUpdate: res.result.summary.autoUpdate,
        });
        setPageInfo(res.page);
      },
      onError: () => {
        initialData();
      },
      onSettled: () => {
        setPending(false);
      },
    }
  );
  const refetch = useCallback(() => {
    refetchRecorder();
  }, [refetchRecorder]);
  useStompDebouncedRefetch({
    selector: (state) =>
      ({
        recorderListUpdated: state.stomp.recorderListUpdated,
        firmwareUpgrade: state.stomp.firmwareUpgrade,
        subscriptionActivatedUpdated:
          state.subscriptionStomp.subscriptionActivatedUpdated,
      } as Record<string, unknown>),
    stompKey: [
      "recorderListUpdated",
      "firmwareUpdated",
    ] as StompDateSignalKey[],
    refetch: refetch,
  });

  const covaDownloadError = useCallback(
    (err: CustomError) => {
      handleErrorNotify(
        err,
        lang.label.report.notify.inventory.download.config.error
      );
    },
    [handleErrorNotify]
  );
  const mutationDownloadInventoryConfig = useStatusMutation(
    postInventoryRecorderConfig,
    {
      onSuccess: (res: RecorderConfigResponse) => {
        const result = res?.result;

        if (res.error !== 0 || !result || !result.url || !result.fileName) {
          notifyError(lang.label.report.notify.inventory.download.config.error);
          return;
        }

        downloadFromUrl(res.result.url, res.result.fileName)
          .then(() => {
            setIsConfigureInfo({ ...res.result });
          })
          .catch((err) => {
            covaDownloadError(err);
          });
      },
      onError: (err) => {
        covaDownloadError(err);
      },
    }
  );

  const onMutateDownloadConfig = (recorderId: string) => {
    if (!mutationDownloadInventoryConfig.idle) {
      return;
    }
    mutationDownloadInventoryConfig.mutate({ recorderId });
  };

  const onHeaderCell = (column: ColumnType<DataRow>) => ({
    onClick: () => {
      if (column.key) {
        updateQueryInfo({
          sortType: column.key as string,
          sortDirection: onSortAntd(recorderQueryInfo.sortDirection),
        });
      }
    },
  });
  const renderTooltip = (row: MergedRecorder) => {
    const version = configDownloadVersion.data?.result?.minVersion;
    return version &&
      !isVersionGreater({
        installedVersion: row.firmwareVersion,
        latestVersion: version,
      })
      ? `Requires ${version} or later`
      : "This file is automatically updated daily at 12:00 PM UTC.";
  };
  const columns: TableColumnsType<DataRow> = [
    {
      key: "type",
      title: "Type",
      width: isMobile ? 90 : 130,
      render: (value, row) => (
        <Type type={convertType(row)} licenseType={row.licenseType} />
      ),
    },
    {
      title: "Title",
      sorter: true,
      key: "mergedSystemName",
      defaultSortOrder: "ascend",
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "mergedSystemName"),
      onHeaderCell: onHeaderCell,
      render: (value, row) =>
        row.recorderCount !== undefined && row.recorderCount > 0 ? (
          <TableTitle row={row} cloudStatus={status.cloudStatus} />
        ) : (
          <Fragment />
        ),

      ellipsis: true,
    },
    {
      key: "access",
      title: "Access",
      render: (value, row) => (
        <s.WebAppRow
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          <WebAppLink
            {...row}
            displayType={convertType(row)}
            accountId={selectedAccount.accountId}
            recorderId={row.recordId || ""}
            cloudStatus={status.cloudStatus}
          />
        </s.WebAppRow>
      ),

      hidden:
        selectedAccount.accountLevel !== LEVEL_TYPE.EU ||
        selectedAccount.isHidden ||
        !selectedAccount.isEnabled,
      width: isMobile ? 120 : "10%",
    },
    {
      title: "Version",
      render: (value, row) => row.version,
      responsive: ["xl"],
      width: isBigScreen ? "15%" : "10%",
    },
    {
      title: "Registered Date",
      render: (value, row) =>
        getAccountTimeFormatDate(
          row.createdAt || "",
          selectedAccount,
          true,
          false
        ),
      responsive: ["lg"],
      width: isBigScreen ? "15%" : 150,
      sorter: true,
      key: "createdAt",
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "createdAt"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Last Updated Date",
      render: (value, row) =>
        getAccountTimeFormatDate(
          row.configFileUpdatedAt || "",
          selectedAccount,
          true,
          false
        ),
      responsive: ["lg"],
      width: isBigScreen ? "15%" : 150,
      sorter: true,
      key: "lastUpdatedAt",
      sortOrder: changeSortOrderAntd(recorderQueryInfo, "lastUpdatedAt"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "",
      width: isMobile ? 60 : 120,
      render: (row, index) => (
        <s.Row className="table-button-wrapper">
          <TableButton
            label={
              <WithTooltip tooltip={renderTooltip(row)}>
                <span>
                  Download {row.isConfig ? <FaDownload /> : <FaInfoCircle />}
                </span>
              </WithTooltip>
            }
            onClick={(e) => {
              e.stopPropagation();
              onMutateDownloadConfig(row.mergedSystemId);
            }}
            disabled={!row.isConfig}
            data-testid={`maintenance-configuration-download-${index}`}
          />
        </s.Row>
      ),

      key: "edit",
      align: "right",
    },
  ];

  const [pending, setPending] = useState<boolean>(true);

  const onChangePage = (page: number, totalRows: number) => {
    updateQueryInfo({ pageNum: page - 1 });
  };

  const onOfflineStatus = useMemo(() => {
    return {
      ...status,
      items: selectedAccount.isEnabled ? status.items : [],
    };
  }, [selectedAccount.isEnabled, status]);

  const onConfigureModal = () => {
    setIsConfigureInfo(null);
  };
  const onClickSearch = (keyword: string) => {
    updateQueryInfo({ keyword: keyword, pageNum: 0 });
  };
  useEffect(() => {
    const centerChildren = <Status status={onOfflineStatus} />;
    setCenterChildren(centerChildren);
  }, [onOfflineStatus, setCenterChildren]);

  return (
    <ConfigurationPresenter
      data={recorder}
      columns={columns as TableColumnsType<DataRow>}
      paginationInfo={recorderQueryInfo}
      onChangePage={onChangePage}
      initialPending={pending}
      configureInfo={configureInfo}
      onConfigureModal={onConfigureModal}
      centerChildren={centerChildren}
      keyword={searchParams.get("keyword") ?? ""}
      onClickSearch={onClickSearch}
    />
  );
}
